import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import LabeledInputWithErrors from './Form/LabeledInputWithErrors';
import { showPhoneWithoutCountryCode } from './helpers';
import { useDebounceSelector } from './hooks/useDebounceSelector';
import { useFormHandlers } from './hooks/useFormHandlers';

import { isMAIPasPDF } from 'businessLogic/Payment/helpers';
import Button from 'components/Shared/Button/Button';
import {
  frictionlessCheckoutContrySelector,
  frictionlessCheckoutEmailSelector,
  frictionlessCheckoutPhoneSelector,
  isFrictionlessCheckoutEmailValidSelector,
  isFrictionlessCheckoutPhoneValidSelector,
} from 'store/auth/selectors';
import { authActions } from 'store/auth/slice';
import { countrySelector } from 'store/payment/selectors';
import { setContactDetailsEmail, validateContactDetailsEmail } from 'store/payment/slice';
import { saleSelectors } from 'store/sale/selectors';
import { useSelector } from 'store/utils';

interface FrictionlessDetailsUpdateProps {
  updateFrictionlessLoginCountry?: (country: Record<string, any>) => void;
  cdn?: string;
}

const FrictionlessDetailsUpdate: React.FC<FrictionlessDetailsUpdateProps> = ({
  updateFrictionlessLoginCountry,
  cdn,
}) => {
  const dispatch = useDispatch();
  const frictionlessCheckoutEmail = useSelector(frictionlessCheckoutEmailSelector);
  const frictionlessCheckoutPhone = useSelector(frictionlessCheckoutPhoneSelector);
  const country = useSelector(countrySelector);
  const frictionlessCheckoutCountry = useSelector(frictionlessCheckoutContrySelector);
  const countryField = frictionlessCheckoutCountry.a3 ? frictionlessCheckoutCountry : country;
  const isEmailValid = useDebounceSelector(isFrictionlessCheckoutEmailValidSelector);
  const isPhoneValid = useDebounceSelector(isFrictionlessCheckoutPhoneValidSelector);
  const phone = showPhoneWithoutCountryCode({
    phone: frictionlessCheckoutPhone,
    frictionlessCheckoutCountry,
  });
  const [showEmail, setShowEmail] = useState(false);
  const [showPhone, setShowPhone] = useState(false);

  const { sale } = useSelector((state) => ({
    featureFlags: state.featureFlags,
    sale: state.sale,
  }));
  const isMaipASPDFEnabled = useMemo(() => {
    return isMAIPasPDF({ sale });
  }, [sale]);
  const isOneToMany = useSelector((state) => saleSelectors.isOneToManyPaymentRequest(state.sale));
  const { handleBlur, handleChange } = useFormHandlers();
  const intl = useIntl();

  useEffect(() => {
    if (!isEmailValid) {
      setShowEmail(true);
    }
    if (!isPhoneValid) {
      setShowPhone(true);
    }
  }, []);

  const formIsValid = isEmailValid && isPhoneValid;

  const handleSubmit = () => {
    if (formIsValid) {
      dispatch(
        authActions.setFrictionlessCheckoutDetailsRequired({
          isDetailsRequired: false,
        })
      );
    }
  };

  const handleEmailChange = useCallback(
    (value: string) => {
      handleChange(value, 'email');
      dispatch(authActions.setFrictionlessCheckoutEmail({ email: value }));
      if (isMaipASPDFEnabled || isOneToMany) {
        // @ts-ignore
        dispatch(setContactDetailsEmail(value));
        // @ts-ignore
        dispatch(validateContactDetailsEmail());
      }
    },
    [handleChange, dispatch, isMaipASPDFEnabled]
  );
  const handePhoneChange = (value: string) => {
    const phoneWithCountryCode = `${frictionlessCheckoutCountry?.dial_code || '+1'} ${value}`;
    handleChange(phoneWithCountryCode, 'phone');
  };

  return (
    <Fragment>
      <div className="frictionless-details-update">
        <div className="container">
          {showEmail && (
            <div>
              <LabeledInputWithErrors
                label={<FormattedMessage id="FRICTIONLESS_EMAIL" defaultMessage="Email" />}
                type="email"
                name="email"
                value={frictionlessCheckoutEmail}
                error={
                  !isEmailValid
                    ? intl.formatMessage({
                        id: 'FRICTIONLESS_INVALID_EMAIL',
                        defaultMessage: 'Invalid email',
                      })
                    : ''
                }
                onChange={(e) => handleEmailChange(e.target.value)}
                onBlur={() => handleBlur('email')}
                placeholder="Enter your email address"
              />
            </div>
          )}
          {showPhone && (
            <div>
              <LabeledInputWithErrors
                label={<FormattedMessage id="FRICTIONLESS_PHONE" defaultMessage="Phone number" />}
                type="tel"
                name="phone"
                value={phone}
                error={
                  !isPhoneValid && frictionlessCheckoutPhone.trimEnd() !== '+1'
                    ? intl.formatMessage({
                        id: 'FRICTIONLESS_INVALID_PHONE_NUMBER',
                        defaultMessage: 'Invalid phone number',
                      })
                    : ''
                }
                onChange={(e) => handePhoneChange(e.target.value)}
                onBlur={() => handleBlur('phone')}
                placeholder="(555) 555-5555"
                cdn={cdn}
                countryField={countryField}
                updateCountry={updateFrictionlessLoginCountry}
              />
            </div>
          )}
        </div>
        <div className="submit-btn">
          <Button onClick={handleSubmit} buttonType="primary" disabled={!formIsValid} width="100%">
            <FormattedMessage
              id="FRICTIONLESS_DETAILS_UPDATE_BUTTON"
              defaultMessage="Update my details"
            />
          </Button>
        </div>
      </div>
      {/*language=SCSS*/}
      <style jsx>{`
        .frictionless-details-update {
          .container {
            text-align: left;
            display: flex;
            flex-direction: column;
            gap: 10px;
          }
          .submit-btn {
            margin-top: 10px;
          }
        }
      `}</style>
    </Fragment>
  );
};

export { FrictionlessDetailsUpdate };
